import React, { useState, useEffect } from 'react';
import {
	Box,
	Paper,
	Typography,
	LinearProgress,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
	Grid,
	Button as MuiButton,
	Alert,
	Chip,
	Checkbox,
} from '@mui/material';
import {
	CheckCircle as CheckCircleIcon,
	PendingActions as PendingIcon,
	Download as DownloadIcon,
	Verified as VerifiedIcon,
} from '@mui/icons-material';
import {
	OnboardingEmployeeDocument,
	getOnboardingEmployeeDocuments,
	getOnboardingDocuments,
	extractIdFromIri,
	OnboardingRecord,
	approveOnboarding,
	deleteOnboarding,
	resetOnboarding,
	resetOnboardingPartial,
	uploadAttestedDocument,
} from 'core/API/onboarding';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useUserData } from 'store/useUserData';
import {
	resolveOnboardingPermissions,
	canDeleteOnboardingRecord,
	canExecuteOnboardingRecord,
	getCurrentUserId,
} from '../utils/onboardingPermission';
import { SecurityRolePermission } from 'tests/services/permissionService';
import { Button } from '../../../components/ui/button';
import style from './OnboardingDashboard.module.scss';
import { capitalizeWords } from '../../../shared/utils/stringUtils';
import { ManagerAttestationModal, AttestableDocument } from '../components/ManagerAttestationModal';

interface EmployeeOnboardingReviewProps {
	record: OnboardingRecord;
	onBack: () => void;
	onRefresh?: () => Promise<void>;
	canExecuteOnboarding?: boolean;
	canDeleteOnboarding?: boolean;
}

export default function EmployeeOnboardingReview({
	record,
	onBack,
	onRefresh,
	canExecuteOnboarding: canExecuteOnboardingProp,
	canDeleteOnboarding: canDeleteOnboardingProp,
}: EmployeeOnboardingReviewProps): JSX.Element {
	const [isLoading, setIsLoading] = useState(true);
	const [isProcessing, setIsProcessing] = useState(false);
	const [employeeDocuments, setEmployeeDocuments] = useState<OnboardingEmployeeDocument[]>([]);
	const [documentTemplates, setDocumentTemplates] = useState<IOnboardingDocument[]>([]);
	const { showToast } = useToastContext();
	const { currentUserRole } = useUserData();
	const onboardingPermissions = resolveOnboardingPermissions(
		(currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
	);
	const currentUserId = getCurrentUserId();
	const canExecuteOnboarding =
		canExecuteOnboardingProp ??
		canExecuteOnboardingRecord(onboardingPermissions, record, currentUserId);
	const canDeleteOnboarding =
		canDeleteOnboardingProp ??
		canDeleteOnboardingRecord(onboardingPermissions, record, currentUserId);

	// Partial reset selection state
	const [isSelectingResetDocuments, setIsSelectingResetDocuments] = useState(false);
	const [resetTemplateIds, setResetTemplateIds] = useState<Set<number>>(new Set());

	// Attestation modal state
	const [attestationModalOpen, setAttestationModalOpen] = useState(false);
	const [selectedDocForAttestation, setSelectedDocForAttestation] =
		useState<AttestableDocument | null>(null);
	const [selectedDocUrl, setSelectedDocUrl] = useState<string | null>(null);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		fetchOnboardingProgress();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [record.id]);

	const fetchOnboardingProgress = async () => {
		setIsLoading(true);
		try {
			// Fetch employee documents and templates
			const [employeeDocs, templates] = await Promise.all([
				getOnboardingEmployeeDocuments(record.id),
				getOnboardingDocuments(),
			]);

			setEmployeeDocuments(employeeDocs);
			setDocumentTemplates(templates);

			// Note: Phase two status is now embedded in the record.phaseTwoStatuses
			// No need to fetch it separately
		} catch (error) {
			console.error('Error fetching onboarding progress:', error);
			showToast('error', 'Failed to load onboarding progress');
		} finally {
			setIsLoading(false);
		}
	};

	// Attestation handlers
	const handleOpenAttestationModal = (doc: OnboardingEmployeeDocument, templateName: string) => {
		setSelectedDocForAttestation({
			documentId: doc.id,
			documentName: templateName,
		});
		const fullDocUrl = `${process.env.REACT_APP_IMAGE_URL}/onboarding_employee/${doc.filePath}`;
		setSelectedDocUrl(fullDocUrl);
		setAttestationModalOpen(true);
	};

	const handleCloseAttestationModal = () => {
		setAttestationModalOpen(false);
		setSelectedDocForAttestation(null);
		setSelectedDocUrl(null);
	};

	const handleAttestDocument = async (
		documentId: string | number,
		attestedFile: File
	) => {
		if (!canExecuteOnboarding) {
			showToast('error', 'You do not have permission to attest onboarding documents');
			return;
		}

		try {
			await uploadAttestedDocument(
				attestedFile,
				record.id,
				documentId
			);

			setEmployeeDocuments((prev) =>
				prev.map((doc) =>
					doc.id === documentId
						? {
								...doc,
								managerApproved: true,
								managerApprovedAt: new Date().toISOString(),
							}
						: doc
				)
			);

			showToast('success', 'Document attested successfully');
		} catch (error) {
			console.error('Error attesting document:', error);
			throw error;
		}
	};

	const handleDownloadDocument = (doc: OnboardingEmployeeDocument) => {
		const url = `${process.env.REACT_APP_IMAGE_URL}/onboarding_employee/${doc.filePath}`;
		const link = document.createElement('a');
		link.href = url;
		link.download = doc.filePath.split('/').pop() || 'document.pdf';
		link.target = '_blank';
		link.click();
		showToast('success', 'Document download started');
	};

	const handleApprove = async () => {
		if (!canExecuteOnboarding) {
			showToast('error', 'You do not have permission to approve onboarding');
			return;
		}

		// Check if all required documents are attested
		if (!allDocumentsAttested) {
			showToast('error', 'Please attest all required documents before approving');
			return;
		}

		if (!window.confirm('Are you sure you want to approve this onboarding?')) {
			return;
		}

		setIsProcessing(true);
		try {
			await approveOnboarding(record.id);
			showToast('success', 'Onboarding approved successfully');
			if (onRefresh) await onRefresh();
			onBack();
		} catch (error) {
			console.error('Error approving onboarding:', error);
			showToast('error', 'Failed to approve onboarding');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleReject = async () => {
		if (!canExecuteOnboarding || !canDeleteOnboarding) {
			showToast('error', 'You do not have permission to reject onboarding');
			return;
		}

		if (
			!window.confirm(
				'Are you sure you want to cancel this onboarding? This will permanently delete the onboarding and the associated user.'
			)
		) {
			return;
		}

		setIsProcessing(true);
		try {
			await deleteOnboarding(record.id);
			showToast('success', 'Onboarding cancelled successfully');
			if (onRefresh) await onRefresh();
			onBack();
		} catch (error) {
			console.error('Error cancelling onboarding:', error);
			showToast('error', 'Failed to cancel onboarding');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleOpenResetSelection = () => {
		if (!canExecuteOnboarding) {
			showToast('error', 'You do not have permission to reset onboarding');
			return;
		}

		setResetTemplateIds(new Set());
		setIsSelectingResetDocuments(true);
	};

	const handleCancelResetSelection = () => {
		setIsSelectingResetDocuments(false);
		setResetTemplateIds(new Set());
	};

	const handleToggleResetDocument = (templateId: number) => {
		setResetTemplateIds((prev) => {
			const next = new Set(prev);
			if (next.has(templateId)) {
				next.delete(templateId);
			} else {
				next.add(templateId);
			}
			return next;
		});
	};

	const handleConfirmReset = async () => {
		if (!canExecuteOnboarding) {
			showToast('error', 'You do not have permission to reset onboarding');
			return;
		}

		const isPartial = resetTemplateIds.size > 0;
		const confirmMsg = isPartial
			? `Are you sure you want to reset ${resetTemplateIds.size} selected document(s)? The employee will need to re-fill only those documents.`
			: 'No documents selected. This will perform a full reset — the employee will need to fill out all forms again. Continue?';

		if (!window.confirm(confirmMsg)) {
			return;
		}

		setIsProcessing(true);
		try {
			if (isPartial) {
				await resetOnboardingPartial(record.id, Array.from(resetTemplateIds));
				showToast('success', 'Selected documents reset successfully');
			} else {
				await resetOnboarding(record.id);
				showToast('success', 'Onboarding reset successfully');
			}
			setIsSelectingResetDocuments(false);
			setResetTemplateIds(new Set());
			if (onRefresh) await onRefresh();
			onBack();
		} catch (error) {
			console.error('Error resetting onboarding:', error);
			showToast('error', 'Failed to reset onboarding');
		} finally {
			setIsProcessing(false);
		}
	};

	// Map phase2 status documents to templates
	// Start from phaseTwoStatuses.progress.documents and find matching templates
	const documentsWithStatus = (record.phaseTwoStatuses?.[0]?.progress?.documents || [])
		.map((phaseDoc) => {
			// Find the matching template from documentTemplates
			const template = documentTemplates.find((t) => t.id === phaseDoc.template_id);
			
			// If template not found, skip this document
			if (!template) {
				return null;
			}

			// Find the uploaded employee document
			const uploadedDoc = employeeDocuments.find((doc) => {
				// Handle both string IRI and object template
				const templateId = typeof doc.template === 'string' 
					? extractIdFromIri(doc.template) 
					: doc.template.id;
				return templateId === template.id;
			});

			// Use status from phase two statuses
			const status = phaseDoc.status || 'pending';
			const completed = status === 'completed' || status === 'approved' || !!uploadedDoc;

			return {
				template,
				uploaded: completed,
				uploadedDocument: uploadedDoc,
				status,
			};
		})
		.filter((doc) => doc !== null) as Array<{
			template: IOnboardingDocument;
			uploaded: boolean;
			uploadedDocument?: OnboardingEmployeeDocument;
			status: string;
		}>;

	const completedCount = documentsWithStatus.filter((d) => d.uploaded).length;
	const totalCount = documentsWithStatus.length;
	const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

	// Check if basic info is completed
	// Use phaseTwoStatuses if available, otherwise fallback to manual check
	const basicInfoCompleted = record.phaseTwoStatuses?.[0]?.progress?.basic_info?.status
		? record.phaseTwoStatuses[0].progress.basic_info.status === 'completed' ||
			record.phaseTwoStatuses[0].progress.basic_info.status === 'approved'
		: !!(
				record.gender &&
				record.dateOfBirth &&
				record.addressLine1 &&
				record.city &&
				record.state &&
				record.postcode
			);

	// Check if all documents requiring attestation have been attested
	// Use template's isManagerSignatureRequired field
	const documentsRequiringAttestation = documentsWithStatus.filter((doc) => {
		return doc.template.isManagerSignatureRequired && doc.uploaded;
	});
	const allDocumentsAttested =
		documentsRequiringAttestation.length === 0 ||
		documentsRequiringAttestation.every(
			(doc) => doc.uploadedDocument?.managerApproved
		);

	const getStatusIcon = (completed: boolean) => {
		return completed ? (
			<CheckCircleIcon color="success" />
		) : (
			<PendingIcon sx={{ color: 'rgb(68, 99, 136)' }} />
		);
	};

	if (isLoading) {
		return (
			<Box
				sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			{/* Back Button */}
			<Box sx={{ mb: 3 }}>
				<Button onClick={onBack} className={style.BackButton}>
					Back
				</Button>
			</Box>

			{/* Employee Info Header */}
			<Paper sx={{ p: 3, mb: 3 }}>
				<Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
					Onboarding Progress: {record.firstName} {record.lastName}
				</Typography>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Email
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{record.email}
						</Typography>
					</Grid>
					{/* <Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Phone
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{record.cellPhone}
						</Typography>
					</Grid> */}
					<Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Employment Start Date
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{record.employmentStartDate
								? new Date(record.employmentStartDate).toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
									})
								: 'N/A'}
						</Typography>
					</Grid>
					<Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Type
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{capitalizeWords(record.type)}
						</Typography>
					</Grid>
					<Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Status
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{capitalizeWords(record.status)}
						</Typography>
					</Grid>
					<Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Pay Type
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{capitalizeWords(record.payType)}
						</Typography>
					</Grid>
					<Grid item xs={12} md={4}>
						<Typography variant="caption" color="text.secondary">
							Employment Type
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{capitalizeWords(record.employmentType)}
						</Typography>
					</Grid>
				</Grid>
			</Paper>

			{/* Phase 2: Employee Information & Documents */}
			{(basicInfoCompleted || documentsWithStatus.length > 0) && (
				<Paper sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
						Phase 2: Employee Submitted Information
					</Typography>

					{/* Overall Progress Status */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							mb: 3,
							p: 2,
							backgroundColor: '#f5f5f5',
							borderRadius: 1,
						}}
					>
						{getStatusIcon(basicInfoCompleted)}
						<Typography variant="body1" sx={{ ml: 2, fontWeight: 600 }}>
							Basic Information Status
						</Typography>
						<Box sx={{ ml: 'auto' }}>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{basicInfoCompleted ? 'Completed' : 'Pending'}
							</Typography>
						</Box>
					</Box>

					{basicInfoCompleted && (
						<>
							<Grid container spacing={3}>
								{/* Personal Information */}
								<Grid item xs={12}>
									<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
										Personal Information
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										First Name
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.firstName || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Middle Name
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.middleName || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Last Name
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.lastName || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Cell Phone
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.cellPhone || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Gender
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.gender || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Marital Status
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.maritalStatus || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Date of Birth
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.dateOfBirth
											? new Date(record.dateOfBirth).toLocaleDateString('en-US', {
													year: 'numeric',
													month: 'long',
													day: 'numeric',
												})
											: 'Not Provided'}
									</Typography>
								</Grid>

								{/* Address Information */}
								<Grid item xs={12} sx={{ mt: 2 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
										Address Information
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Address Line 1
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.addressLine1 || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Address Line 2
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.addressLine2 || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										City
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.city || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										State
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.state || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Postal Code
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.postcode || 'Not Provided'}
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										Country
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 500 }}>
										{record.country || 'Not Provided'}
									</Typography>
								</Grid>
							</Grid>
						</>
					)}

					{/* Documents Section */}
					<Box sx={{ mt: basicInfoCompleted ? 4 : 0 }}>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
							Submitted Documents
						</Typography>

						{/* Document Progress Bar */}
						<Box sx={{ mb: 3 }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
								<Typography variant="body2" color="text.secondary">
									Documents: {completedCount} of {totalCount} completed
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{progressPercent}%
								</Typography>
							</Box>
							<LinearProgress
								variant="determinate"
								value={progressPercent}
								sx={{ height: 8, borderRadius: 4 }}
							/>
						</Box>

						{documentsWithStatus.length === 0 ? (
							<Box sx={{ textAlign: 'center', py: 4 }}>
								<Typography color="text.secondary">No documents configured</Typography>
							</Box>
						) : (
							<>
								{isSelectingResetDocuments && (
									<Alert severity="info" sx={{ mb: 2 }}>
										Select the documents that need to be reset. Leave all unchecked to perform a full reset.
									</Alert>
								)}
							<List>
								{documentsWithStatus.map((doc, idx) => (
									<React.Fragment key={doc.template.id}>
										<ListItem
											sx={{
												display: 'flex',
												alignItems: 'center',
												py: 2,
											}}
										>
											{isSelectingResetDocuments && doc.template.id != null && (
												<Checkbox
													checked={resetTemplateIds.has(doc.template.id!)}
													onChange={() => handleToggleResetDocument(doc.template.id!)}
													sx={{
														mr: 1,
														color: 'rgb(68, 99, 136)',
														'&.Mui-checked': { color: 'rgb(68, 99, 136)' },
													}}
												/>
											)}
											<ListItemIcon>{getStatusIcon(doc.uploaded)}</ListItemIcon>
											<ListItemText
												primary={doc.template.name}
												secondary={
												doc.status && doc.status !== 'pending' ? (
													<Typography
														component="span"
														variant="caption"
														sx={{ fontStyle: 'italic' }}
													>
														({doc.status})
													</Typography>
												) : undefined
											}
										/>

										<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
											{/* Show status based on attestation requirement */}
											{doc.uploaded ? (
												doc.template.isManagerSignatureRequired ? (
													doc.uploadedDocument?.managerApproved ? (
														<>
															<Chip
																icon={<VerifiedIcon />}
																label="Attested"
																color="success"
																size="small"
															/>
															{doc.uploadedDocument.managerApprovedAt && (
																<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
																	{new Date(doc.uploadedDocument.managerApprovedAt).toLocaleDateString()}
																</Typography>
															)}
														</>
													) : (
														<>
															<Chip
																icon={<PendingIcon />}
																label="Attestation Required"
																color="warning"
																size="small"
															/>
															{canExecuteOnboarding && (
																<MuiButton
																	variant="contained"
																	size="small"
																	startIcon={<VerifiedIcon />}
																	onClick={() =>
																		handleOpenAttestationModal(doc.uploadedDocument!, doc.template.name)
																	}
																	disabled={isProcessing}
																	sx={{
																		backgroundColor: 'rgb(68, 99, 136)',
																		color: '#fff',
																		'&:hover': {
																			backgroundColor: 'rgb(58, 89, 126)',
																		},
																	}}
																>
																	Attest
																</MuiButton>
															)}
														</>
													)
												) : (
													<Chip
														icon={<CheckCircleIcon />}
														label="Completed"
														color="success"
														size="small"
													/>
												)
											) : (
												<Chip
													icon={<PendingIcon />}
													label="Pending"
													color="success"
													size="small"
												/>
											)}

											{/* Download Button */}
											{doc.uploaded && doc.uploadedDocument && (
												<MuiButton
													variant="contained"
													size="small"
													startIcon={<DownloadIcon />}
													onClick={() => handleDownloadDocument(doc.uploadedDocument!)}
													sx={{
														// ml: 2,
														backgroundColor: 'rgb(68, 99, 136)',
														color: '#fff',
														'&:hover': {
															backgroundColor: 'rgb(58, 89, 126)',
														},
													}}
												>
													Download
												</MuiButton>
											)}
										</Box>
										</ListItem>
										{idx < documentsWithStatus.length - 1 && <Divider />}
									</React.Fragment>
								))}
							</List>
							</>
						)}
					</Box>
				</Paper>
			)}

		{/* Manager Review Actions - Requires execute permission */}
		{canExecuteOnboarding &&
			record.phase2CompletedAt &&
			(!record.phase2ReviewedAt || record.phase2ReviewStatus === 'pending_approval') && (
				<Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
						Manager Review Actions
					</Typography>
					{/* Attestation Warning */}
					{documentsRequiringAttestation.length > 0 && !allDocumentsAttested && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							<Typography variant="body2" fontWeight={600}>
								Document Attestation Required
							</Typography>
							<Typography variant="body2">
								Please attest{' '}
								{
								documentsRequiringAttestation.filter(
									(d) => !d.uploadedDocument?.managerApproved
								).length
							}{' '}
							document
							{documentsRequiringAttestation.filter(
								(d) => !d.uploadedDocument?.managerApproved
							).length > 1
									? 's'
									: ''}{' '}
								before approving onboarding.
							</Typography>
						</Alert>
					)}

					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Review the employee&apos;s submitted information and documents, then take an action
						below.
					</Typography>

					{isSelectingResetDocuments ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Alert severity="warning">
								<Typography variant="body2" fontWeight={600}>
									Partial Reset Mode
								</Typography>
								<Typography variant="body2">
									{resetTemplateIds.size > 0
										? `${resetTemplateIds.size} document(s) selected for reset. The employee will re-fill only these documents.`
										: 'No documents selected — confirming will perform a full reset.'}
								</Typography>
							</Alert>
							<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
								<MuiButton
									variant="outlined"
									onClick={handleCancelResetSelection}
									disabled={isProcessing}
								>
									Cancel
								</MuiButton>
								<MuiButton
									variant="contained"
									color="warning"
									onClick={handleConfirmReset}
									disabled={isProcessing}
								>
									{isProcessing
										? 'Processing...'
										: resetTemplateIds.size > 0
										? `Reset ${resetTemplateIds.size} Document(s)`
										: 'Full Reset'}
								</MuiButton>
							</Box>
						</Box>
					) : (
						<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
							<MuiButton
								variant="outlined"
								color="warning"
								onClick={handleOpenResetSelection}
								disabled={isProcessing}
							>
								Reset to Phase 2
							</MuiButton>
							{canDeleteOnboarding && (
								<MuiButton
									variant="outlined"
									color="error"
									onClick={handleReject}
									disabled={isProcessing}
								>
									Reject
								</MuiButton>
							)}
							<MuiButton
								variant="contained"
								color="success"
								onClick={handleApprove}
								disabled={isProcessing}
							>
								{isProcessing ? 'Processing...' : 'Approve'}
							</MuiButton>
						</Box>
					)}
				</Paper>
			)}

			{/* Phase 3 Complete Message */}
			{record.phase3CompletedAt && (
				<Paper sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
					<Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
						✓ Onboarding Complete
					</Typography>
					<Typography variant="body2" color="text.secondary">
						This onboarding was reviewed and completed on{' '}
						{new Date(record.phase3CompletedAt).toLocaleDateString()}.
					</Typography>
				</Paper>
			)}

			{/* Manager Attestation Modal */}
			<ManagerAttestationModal
				open={attestationModalOpen}
				onClose={handleCloseAttestationModal}
				document={selectedDocForAttestation}
				onAttest={handleAttestDocument}
				documentUrl={selectedDocUrl}
			/>
		</Box>
	);
}
